// MainNav Component
// This file defines the main navigation bar for the application, with active link highlighting.

import { styled } from "@stitches/react";
import { Link, useLocation } from "react-router-dom";
import { NavItem } from "@/types/navigation";

/**
 * NavSection
 * Wrapper for the navigation bar, sets background and height.
 */
const NavSection = styled("div", {
  paddingLeft: 8,
  paddingRight: 16,
  backgroundColor: "#F3F3F3",
  height: 70,
});

/**
 * Nav
 * Flex container for the navigation links.
 */
const Nav = styled("nav", {
  display: "flex",
  alignItems: "center",
  height: "100%",
  justifyContent: "space-between",

  "& > ul": {
    margin: "0 24px !important",
  },
});

/**
 * List
 * Styled unordered list for navigation items, with active state styling.
 */
const List = styled("ul", {
  display: "flex",
  listStyleType: "none",
  gap: 40,

  "& > li": {
    fontSize: 16,
    lineHeight: "20px",
  },
  "& > li.active": {
    borderBottom: "1px solid #043961",
  },
  "& > li.active > a": {
    color: "#043961",
  },
});

/**
 * Props for the MainNav component
 * @param navItems - Array of navigation items (id, href, text)
 */
interface Props {
  navItems: NavItem[];
}

/**
 * MainNav Component
 * Renders the main navigation bar with active link highlighting.
 *
 * @param navItems - Array of navigation items
 */
const MainNav = ({ navItems }: Props) => {
  const location = useLocation();
  return (
    <NavSection data-testid="main-nav-wrapper">
      <Nav>
        <List>
          {navItems.map((nav: NavItem) => (
            <li
              key={nav.id}
              className={location.pathname === nav.href ? "active" : undefined}
            >
              <Link to={nav.href}>{nav.text}</Link>
            </li>
          ))}
        </List>
      </Nav>
    </NavSection>
  );
};

export default MainNav;
